import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Folder, Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ProjectSelector({ videoData, onClose, onSaved }) {
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const toggleProject = (projectId) => {
    setSelectedProjects((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save video with selected projects
      const response = await axios.post(
        `${API}/videos`,
        {
          video_title: videoData.video_title,
          video_url: videoData.video_url,
          platform: videoData.platform,
          transcription: videoData.transcription,
          bullet_summary: videoData.bullet_summary,
          structured_summary: videoData.structured_summary,
          project_ids: selectedProjects
        },
        { withCredentials: true }
      );

      toast.success("Video saved to library!");
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error("Error saving video:", error);
      toast.error(error.response?.data?.detail || "Failed to save video");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" data-testid="project-selector-modal">
      <div className="modal-content project-selector-modal">
        <div className="modal-header">
          <h2>Save to Library</h2>
          <button onClick={onClose} className="close-button" data-testid="close-modal">
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-state">
              <Loader2 className="animate-spin" size={32} />
              <p>Loading projects...</p>
            </div>
          ) : (
            <>
              <p className="modal-description">
                Select projects to organize this video
              </p>

              {projects.length === 0 ? (
                <div className="empty-projects">
                  <Folder size={48} className="empty-icon" />
                  <p>No projects yet</p>
                  <p className="empty-hint">
                    Create projects from the Projects page to organize your videos
                  </p>
                </div>
              ) : (
                <div className="projects-list">
                  {projects.map((project) => (
                    <div key={project.id} className="project-item">
                      <Checkbox
                        id={`project-${project.id}`}
                        checked={selectedProjects.includes(project.id)}
                        onCheckedChange={() => toggleProject(project.id)}
                        data-testid={`project-checkbox-${project.id}`}
                      />
                      <Label
                        htmlFor={`project-${project.id}`}
                        className="project-label"
                      >
                        <div
                          className="project-color-dot"
                          style={{ backgroundColor: project.color }}
                        />
                        {project.name}
                      </Label>
                    </div>
                  ))}
                </div>
              )}

              <div className="modal-actions">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={saving}
                >
                  Skip for Now
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="save-button"
                  data-testid="save-to-library-button"
                >
                  {saving ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Folder size={16} />
                      Save to Library
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectSelector;
